import React, { useContext, useEffect, useState } from 'react';
import {
  actionConfig,
  WorkFlowActionContext,
  WorkFlowContext,
  WorkflowSelectModalContext,
} from '../../../../utils/context';
import {
  getAllAssistantsInFlow,
  getAllGatewaysInFlow,
  getAllPublicAssistantsInFlow,
  mapDynamicOptions,
} from '../services';
import { getProviders } from '../../llm/services/llm';
import makeToast from '../../../../components/toaster';
import {
  getAllPermissionsOfLlms,
  loadAllPermissionsOfLlms,
  LLM_PERMISSIONS_INVALIDATED_EVENT,
} from '../../../../services';
import { providersTypes } from '../../llm/pages/llm.props';
import RenderConfig from '../services/render-config';
import { Fields, TriggerType, WorkflowFields, WorkFlowPanelData } from '../data/workflow-data';
import { methods } from '../data/constants';
import { setUpTabProps } from '../automator.props';

function SetUpTab({
  selectedNode,
  setActiveTab,
  modalData,
  activeTab,
  setConfigModule,
  setAssistantData,
  disableMode,
  setLogout,
}: setUpTabProps) {
  const { workFlowData, setWorkFlowData } = useContext(WorkFlowContext);
  const { workFlowActionData, setWorkFlowActionData } = useContext(WorkFlowActionContext);
  const { setWorkflowSelectModalOpen } = useContext(WorkflowSelectModalContext);
  const [selectedValue, setSelectedValue] = useState('');
  const [updatedWorkflowPanelData, setUpdatedWorkflowPanelData] =
    useState<WorkflowFields>(WorkFlowPanelData);
  const triggerType = selectedNode?.data?.trigger_type || '';

  const savedValue = localStorage?.getItem('workFlowData');
  const parsedValue = savedValue ? JSON.parse(savedValue) : {};
  const parsedSavedValue = JSON.parse(savedValue!);

  useEffect(() => {
    // Reset selected value if trigger_catalogue_id is empty
    if (workFlowData.trigger_catalogue_id === '') {
      setSelectedValue('');
    }
  }, [workFlowData]);

  const isButtonDisabled = (() => {
    if (!triggerType) return false;
    const { action_config: actionConfigVal } = workFlowActionData || {};
    let catalogueId = '';
    switch (triggerType) {
      case 'Schedule':
      case 'Webhook API':
        catalogueId = workFlowData?.trigger_catalogue_id as string;
        break;

      case 'Assistants':
        catalogueId = actionConfigVal?.assistant_id as string;
        break;

      case 'Gateways':
        catalogueId = actionConfigVal?.gateway_id as string;
        break;

      case 'API':
        catalogueId = actionConfigVal?.method as string;
        break;
      case 'Send HTTP Request':
        catalogueId = actionConfigVal?.method as string;
        break;

      case 'Send Prompt':
      case 'send prompt':
        catalogueId = actionConfigVal?.model_id as string;
        break;

      default:
        catalogueId = '';
        break;
    }
    if (catalogueId === '' || catalogueId === undefined || catalogueId === null) return true;
    return false;
  })();

  useEffect(() => {
    const selectActionConfig = parsedSavedValue?.actions?.filter(
      (item: { action_order: string; action_id: number }) =>
        item.action_order === selectedNode?.id ||
        (item.action_id && item.action_id === selectedNode?.data.unique_id),
    )?.[0];

    let selValue = '';
    let parsedValueConfig = '';
    if (parsedValue?.trigger && Object.keys(parsedValue?.trigger).length) {
      selValue = parsedSavedValue?.trigger?.trigger_catalogue_id;
      parsedValueConfig = parsedSavedValue?.trigger?.workFlowData;
      setSelectedValue(selValue || '');
      setConfigModule(parsedValueConfig);
    }
    if (parsedValue?.actions && selectActionConfig && Object.keys(selectActionConfig).length) {
      selValue =
        triggerType === 'Gateways'
          ? selectActionConfig?.action_config?.gateway_id
          : selectActionConfig?.action_config?.assistant_id;
      parsedValueConfig =
        triggerType !== 'Gateways'
          ? selectActionConfig?.action_config?.dynamic_variables
          : selectActionConfig?.action_config?.gateway_config;
      setSelectedValue(selValue || '');
      setConfigModule(parsedValueConfig);
    }
  }, [activeTab, selectedNode]);

  useEffect(() => {
    if (modalData) {
      if (triggerType === 'Schedule') {
        const schedulerData = modalData?.find((data) => data.app === 'Schedule');
        const schedulerOptions = schedulerData?.actions?.map((item) => ({
          id: item.id,
          label: item.name,
          description: item.description,
        }));
        mapDynamicOptions(schedulerOptions as object[], setUpdatedWorkflowPanelData, 'Schedule');
        setActiveTab(0);
      } else if (triggerType === 'Webhook API') {
        const webhookData = modalData?.find((data) => data.app === 'Webhook API');
        const webhookOptions =
          webhookData?.actions &&
          webhookData?.actions?.map((item) => ({
            id: item.id,
            label: item.name,
            description: item.description,
          }));
        mapDynamicOptions(webhookOptions as object[], setUpdatedWorkflowPanelData, 'Webhook API');
        setActiveTab(0);
      } else if (triggerType === 'Assistants') {
        const fetchAssistants = async () => {
          try {
            const [privateRes, publicRes] = await Promise.all([
              getAllAssistantsInFlow(),
              getAllPublicAssistantsInFlow(),
            ]);

            if (privateRes.status === 401 || publicRes.status === 401) {
              setLogout(true);
              return;
            }

            const privateAssistants =
              privateRes.status === 200
                ? (privateRes.data?.data?.assistants?.filter(
                    (item: { name: string }) => item.name !== 'chat' && item.name !== 'prompt',
                  ) ?? [])
                : [];

            const publicAssistants =
              publicRes.status === 200 ? (publicRes.data?.data?.assistants ?? []) : [];

            // Transform both datasets
            const transformedAssistants = [...privateAssistants, ...publicAssistants].map(
              (item) => ({
                id: item.id,
                label: item.name,
                description: item.description,
                username: item.user_name,
                prompt: item.prompt,
              }),
            );

            // Remove duplicates by `id`
            const uniqueAssistants: {
              id: number;
              label: string;
              description: string;
              username: string;
              prompt: string;
            }[] = Array.from(new Map(transformedAssistants.map((a) => [a.id, a])).values());

            setAssistantData(uniqueAssistants);
            mapDynamicOptions(uniqueAssistants, setUpdatedWorkflowPanelData, 'Assistants');
          } catch {
            makeToast('error', 'Error in getting assistants');
          }
        };
        fetchAssistants();
      } else if (triggerType === 'Gateways') {
        getAllGatewaysInFlow()
          .then((res) => {
            if (res.status === 200) {
              const data = res?.data?.data?.gateways;
              const gatewayData = data?.map(
                (item: { id: number; name: string; description: string }) => ({
                  id: item.id,
                  label: item.name,
                  description: item.description,
                }),
              );
              mapDynamicOptions(gatewayData, setUpdatedWorkflowPanelData, 'Gateways');
            } else if (res?.status === 401) {
              setLogout(true);
            }
          })
          .catch(() => {
            makeToast('error', 'Error in getting gateways');
          });
      }
    }
  }, [modalData, triggerType, selectedNode, activeTab]);

  useEffect(() => {
    const selectActionConfig = parsedSavedValue?.actions?.filter(
      (item: { action_order: string; action_id: number }) =>
        item.action_order === selectedNode?.id ||
        (item.action_id && item.action_id === selectedNode?.data.unique_id),
    )?.[0];

    if (triggerType === 'Send Prompt' || triggerType === 'send prompt') {
      let firstProviderId: number;
      const permissions = localStorage.getItem('configurations');
      let filteredArray: providersTypes[];
      getProviders()
        .then(async (res) => {
          if (res.status === 200) {
            if (permissions) {
              await loadAllPermissionsOfLlms();
              const chatPermissions = getAllPermissionsOfLlms();
              if (chatPermissions) {
                const uniqueProviders = chatPermissions?.reduce(
                  (
                    acc: { provider_name: string; proverID: number }[],
                    current: { provider_name: string; proverID: number },
                  ) => {
                    if (
                      !acc.find(
                        (item: { provider_name: string }) =>
                          item.provider_name === current.provider_name,
                      )
                    ) {
                      acc.push(current);
                    }
                    return acc;
                  },
                  [],
                );
                filteredArray = res?.data?.data.filter((item: { id: number }) =>
                  uniqueProviders.some((pro) => item.id === pro.proverID),
                );
              }
            }
            mapDynamicOptions(
              filteredArray.map((data) => ({
                id: data.id,
                name: data.provider_name,
                label: data.provider_name,
              })),
              setUpdatedWorkflowPanelData,
              triggerType === 'Send Prompt' ? 'Send Prompt' : 'send prompt',
              'provider_id',
            );
            firstProviderId = filteredArray?.[0]?.id;
            setWorkFlowActionData({
              ...workFlowActionData,
              action_config: {
                ...workFlowActionData?.action_config,
                provider_id: selectActionConfig?.action_config?.provider_id ?? firstProviderId,
              },
            });
          } else if (res?.status === 401) {
            setLogout(true);
          }
        })
        .catch(() => {
          makeToast('error', 'Error in getting providers');
        });
    } else if (triggerType === 'API' || triggerType === 'Send HTTP Request') {
      setWorkFlowActionData({
        ...workFlowActionData,
        action_order: selectedNode?.id,
        action_config: {
          ...workFlowActionData?.action_config,
          method: methods[0].id,
        },
      });
    }
  }, [triggerType]);

  const [models, setModels] = useState<ReturnType<typeof getAllPermissionsOfLlms>>([]);
  useEffect(() => {
    loadAllPermissionsOfLlms().then(() => setModels(getAllPermissionsOfLlms()));
  }, []);

  useEffect(() => {
    const onInvalidated = () =>
      loadAllPermissionsOfLlms().then(() => setModels(getAllPermissionsOfLlms()));
    window.addEventListener(LLM_PERMISSIONS_INVALIDATED_EVENT, onInvalidated);
    return () => window.removeEventListener(LLM_PERMISSIONS_INVALIDATED_EVENT, onInvalidated);
  }, []);

  useEffect(() => {
    if (triggerType === 'Send Prompt' || (triggerType === 'send prompt' && models)) {
      const modelsData = models.reduce(
        (
          acc: { id: number; name: string; label: string }[],
          model: { proverID: number; id: number; name: string },
        ) => {
          if (model.proverID === workFlowActionData?.action_config?.provider_id) {
            acc.push({
              id: model.id,
              name: model.name,
              label: model.name,
            });
          }
          return acc;
        },
        [],
      );

      mapDynamicOptions(
        modelsData,
        setUpdatedWorkflowPanelData,
        triggerType === 'Send Prompt' ? 'Send Prompt' : 'send prompt',
        'model_id',
      );

      // Update model_id if not already set
      if (modelsData.length > 0 && !workFlowActionData?.action_config?.model_id) {
        setWorkFlowActionData((prev) => ({
          ...prev,
          action_config: {
            ...prev.action_config,
            model_id: modelsData[0].id, // Set the first model's ID
          },
        }));
      }
    }
    if (triggerType === 'API' || triggerType === 'Send HTTP Request') {
      setWorkFlowActionData((prev) => ({
        ...prev,
        action_config: {
          ...prev.action_config,
          method: workFlowActionData?.action_config?.method
            ? workFlowActionData?.action_config?.method
            : methods?.[0]?.id,
        },
      }));
    }
  }, [
    workFlowActionData?.action_config?.provider_id,
    workFlowActionData?.action_config?.method,
    models,
  ]);

  const handleTriggerEvents = (value: string) => {
    setSelectedValue(value);
    if (
      triggerType === 'Assistants' ||
      triggerType === 'Send Prompt' ||
      triggerType === 'send prompt' ||
      triggerType === 'Gateways'
    ) {
      setWorkFlowActionData({
        ...workFlowActionData,
        action_order: selectedNode?.id,
        action_config: {
          ...workFlowActionData?.action_config,
          ...(triggerType === 'Assistants' && { assistant_id: value }),
          ...(triggerType === 'Gateways' && { gateway_id: value }),
        },
      });
    } else {
      setWorkFlowData({
        ...workFlowData,
        trigger_catalogue_id: value,
      });
    }
  };

  const updateActionsInLocalStorage = (workFlowActionDataVal: actionConfig & object) => {
    if (parsedValue?.actions?.length >= 0) {
      const parsedSavedJson = JSON.parse(savedValue!);
      const selectActionConfig = parsedSavedJson?.actions?.filter(
        (item: { action_order: string; action_id: number }) =>
          item.action_order === selectedNode?.id ||
          (item.action_id && item.action_id === selectedNode?.data.unique_id),
      )?.[0];
      // update action
      if (selectActionConfig && Object.keys(selectActionConfig).length) {
        const updatedActions = parsedSavedJson?.actions?.map(
          (item: { action_order: string; action_id: number }) => {
            if (
              item.action_order === selectedNode?.id ||
              item.action_id === selectedNode?.data.unique_id
            ) {
              return {
                ...item, // Keep other properties of the item unchanged
                ...workFlowActionDataVal, // Merge properties from `selectActionConfig`
                isSetUpTabCompleted: true,
              };
            }
            return item; // If condition is not met, return the original item
          },
        );
        localStorage.setItem(
          'workFlowData',
          JSON.stringify({
            ...parsedValue, // Preserve existing keys and values
            actions: updatedActions, // Update the actions array
          }),
        );
      } else {
        // add action
        const updatedActions = [
          ...(parsedValue?.actions || []),
          { ...workFlowActionDataVal, isSetUpTabCompleted: true },
        ];
        localStorage.setItem(
          'workFlowData',
          JSON.stringify({
            ...parsedValue, // Preserve existing keys and values
            actions: updatedActions, // Update the actions array
          }),
        );
      }
    } else {
      localStorage.setItem(
        'workFlowData',
        JSON.stringify({
          ...parsedValue, // Preserve existing data
          actions: [{ ...workFlowActionDataVal, isSetUpTabCompleted: true }],
        }),
      );
    }
  };

  const handleSetUpContinueButton = () => {
    let module;
    const savedDataValue = localStorage?.getItem('workFlowData');
    const parsedDataValue = savedDataValue ? JSON.parse(savedDataValue) : {};
    if (triggerType === 'Schedule') {
      module = modalData
        ?.find((data) => data.app === 'Schedule')
        ?.actions?.find((item) => item.id === workFlowData?.trigger_catalogue_id);
      if (parsedDataValue?.trigger) {
        localStorage?.setItem(
          'workFlowData',
          JSON.stringify({
            ...(parsedDataValue?.actions?.length > 0
              ? { actions: [...(parsedDataValue?.actions ?? [])] }
              : null),
            trigger: {
              ...workFlowData,
              trigger_id: parsedDataValue?.trigger?.trigger_id,
              isSetUpTabCompleted: true,
            },
          }),
        );
      }
    } else if (triggerType === 'Webhook API') {
      module = modalData
        ?.find((data) => data.app === 'Webhook API')
        ?.actions?.find((item) => item.id === workFlowData?.trigger_catalogue_id);
      localStorage?.setItem(
        'workFlowData',
        JSON.stringify({
          ...(parsedDataValue?.actions?.length > 0
            ? { actions: [...(parsedDataValue?.actions ?? [])] }
            : null),
          trigger: {
            ...workFlowData,
            trigger_id: parsedDataValue?.trigger?.trigger_id,
            isSetUpTabCompleted: true,
          },
        }),
      );
    } else if (triggerType === 'Assistants') {
      module = modalData
        ?.find((data) => data.app === 'Assistants')
        ?.actions?.find((item) => item.name === 'Assistant');
      setWorkFlowActionData({
        ...workFlowActionData,
        action_order: selectedNode?.id,
        isSetUpTabCompleted: true,
        action_config: {
          ...workFlowActionData.action_config,
          dynamic_variables: {},
        },
      });
      updateActionsInLocalStorage({
        ...workFlowActionData,
        action_config: {
          ...workFlowActionData.action_config,
          dynamic_variables: {},
        },
      });
    } else if (
      triggerType === 'API' ||
      triggerType === 'Send HTTP Request' ||
      triggerType === 'Send Prompt' ||
      triggerType === 'send prompt' ||
      triggerType === 'Gateways'
    ) {
      setWorkFlowActionData({
        ...workFlowActionData,
        action_order: selectedNode?.id,
        isSetUpTabCompleted: true,
      });
      updateActionsInLocalStorage({
        ...workFlowActionData,
        action_order: selectedNode?.id,
        isSetUpTabCompleted: true,
      });
    }
    setConfigModule(module ?? {});
    setActiveTab(1);
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setWorkFlowActionData({
      ...workFlowActionData,
      action_config: {
        ...workFlowActionData?.action_config,
        [e.target.name]:
          e.target.name === 'provider_id' || e.target.name === 'model_id'
            ? parseInt(e.target.value, 10)
            : e.target.value,
      },
    });
  };

  const getValue = (field: Fields): string => {
    switch (field.props.name) {
      case 'provider_id':
        return workFlowActionData?.action_config?.provider_id as string;
      case 'model_id':
        return workFlowActionData?.action_config?.model_id as string;
      case 'triggerType':
        return triggerType;
      case 'method':
        return workFlowActionData?.action_config?.method as string;
      default:
        return selectedValue;
    }
  };

  return (
    <div className="mt-4 space-y-6">
      {updatedWorkflowPanelData[triggerType as TriggerType]?.setup.map(
        (field: Fields, index: number) => {
          return RenderConfig({
            field,
            index,
            setWorkflowSelectModalOpen,
            handleTriggerEvents,
            value: getValue(field),
            handleChange,
          });
        },
      )}
      {!triggerType && (
        <>No {selectedNode?.data.node_type === 'action' ? 'action' : 'trigger'} selected!</>
      )}

      {/* Continue Button */}
      <button
        type="button"
        className={`w-full py-2 rounded-lg ${isButtonDisabled ? 'bg-[#6B7280]' : 'bg-[#122961]'}`}
        disabled={isButtonDisabled || disableMode}
        onClick={() => handleSetUpContinueButton()}
      >
        <span className="text-white">Continue</span>
      </button>
    </div>
  );
}

export default SetUpTab;
